/**
 * ESC/POS command encoder for thermal printers
 * Reference: https://reference.epson-biz.com/modules/ref_escpos/index.php
 */

// ESC/POS Control Codes
export const ESC = '\x1B';
export const GS = '\x1D';
export const LF = '\x0A';
export const CR = '\x0D';

// ESC/POS Commands
export const ESCPOS = {
  // Initialize printer
  INIT: `${ESC}@`,
  
  // Text formatting
  BOLD_ON: `${ESC}E\x01`,
  BOLD_OFF: `${ESC}E\x00`,
  UNDERLINE_ON: `${ESC}-\x01`,
  UNDERLINE_OFF: `${ESC}-\x00`,
  DOUBLE_HEIGHT_ON: `${ESC}!\x10`,
  DOUBLE_WIDTH_ON: `${ESC}!\x20`,
  DOUBLE_SIZE_ON: `${ESC}!\x30`,
  NORMAL_SIZE: `${ESC}!\x00`,
  
  // Text alignment
  ALIGN_LEFT: `${ESC}a\x00`,
  ALIGN_CENTER: `${ESC}a\x01`,
  ALIGN_RIGHT: `${ESC}a\x02`,
  
  // Line spacing
  LINE_SPACING_DEFAULT: `${ESC}2`,
  LINE_SPACING: (n: number) => `${ESC}3${String.fromCharCode(n)}`,
  
  // Paper control
  FEED_LINES: (n: number) => `${ESC}d${String.fromCharCode(n)}`,
  CUT_FULL: `${GS}V\x00`,
  CUT_PARTIAL: `${GS}V\x01`,
  CUT_FEED_FULL: `${GS}V\x41\x03`,
  CUT_FEED_PARTIAL: `${GS}V\x42\x03`,
  
  // Cash drawer
  DRAWER_KICK_2: `${ESC}p\x00\x19\xFA`,
  DRAWER_KICK_5: `${ESC}p\x01\x19\xFA`,
  
  // Print mode
  MODE_FONT_A: `${ESC}M\x00`,
  MODE_FONT_B: `${ESC}M\x01`,
};

export interface EncoderOptions {
  align?: 'left' | 'center' | 'right';
  bold?: boolean;
  underline?: boolean;
  size?: 1 | 2 | 3;
}

/**
 * Encode text with formatting options
 */
export function encodeText(text: string, options: EncoderOptions = {}): string {
  let result = '';
  
  // Apply alignment
  switch (options.align) {
    case 'center':
      result += ESCPOS.ALIGN_CENTER;
      break;
    case 'right':
      result += ESCPOS.ALIGN_RIGHT;
      break;
    default:
      result += ESCPOS.ALIGN_LEFT;
  }
  
  // Apply bold
  if (options.bold) {
    result += ESCPOS.BOLD_ON;
  }
  
  // Apply underline
  if (options.underline) {
    result += ESCPOS.UNDERLINE_ON;
  }
  
  // Apply size
  switch (options.size) {
    case 2:
      result += ESCPOS.DOUBLE_HEIGHT_ON;
      break;
    case 3:
      result += ESCPOS.DOUBLE_SIZE_ON;
      break;
    default:
      result += ESCPOS.NORMAL_SIZE;
  }
  
  // Add text with line break
  result += text + LF;
  
  // Reset formatting
  result += ESCPOS.NORMAL_SIZE;
  if (options.bold) result += ESCPOS.BOLD_OFF;
  if (options.underline) result += ESCPOS.UNDERLINE_OFF;
  result += ESCPOS.ALIGN_LEFT;
  
  return result;
}

/**
 * Encode a divider line
 */
export function encodeLine(char: string = '-', width: number = 48): string {
  return char.repeat(width) + LF;
}

/**
 * Encode QR Code command (simplified - may need adjustment per printer)
 * Note: Full QR implementation is complex, this is a basic version
 */
export function encodeQrCode(data: string, moduleSize: number = 4): string {
  const dataLength = data.length;
  
  // Function 165 - Set model (Model 2)
  let result = `${GS}(k\x04\x00\x31\x41\x32\x00`;
  
  // Function 167 - Set module size
  result += `${GS}(k\x03\x00\x31\x43${String.fromCharCode(moduleSize)}`;
  
  // Function 169 - Set error correction (L=48, M=49, Q=50, H=51)
  result += `${GS}(k\x03\x00\x31\x45\x31`;
  
  // Function 180 - Store data
  const pL = (dataLength + 3) % 256;
  const pH = Math.floor((dataLength + 3) / 256);
  result += `${GS}(k${String.fromCharCode(pL)}${String.fromCharCode(pH)}\x31\x50\x30${data}`;
  
  // Function 181 - Print stored data
  result += `${GS}(k\x03\x00\x31\x51\x30`;
  
  return result;
}

/**
 * Encode barcode
 */
export function encodeBarcode(
  data: string, 
  type: 'EAN13' | 'CODE128' | 'CODE39' = 'CODE128',
  height: number = 50,
  width: number = 2
): string {
  let result = '';
  
  // Set barcode height
  result += `${GS}h${String.fromCharCode(height)}`;
  
  // Set barcode width
  result += `${GS}w${String.fromCharCode(width)}`;
  
  // Set HRI position (below barcode)
  result += `${GS}H\x02`;
  
  // Set HRI font
  result += `${GS}f\x00`;
  
  // Print barcode based on type
  let typeCode: number;
  switch (type) {
    case 'EAN13':
      typeCode = 67; // EAN13
      break;
    case 'CODE39':
      typeCode = 69; // CODE39
      break;
    case 'CODE128':
    default:
      typeCode = 73; // CODE128
      break;
  }
  
  result += `${GS}k${String.fromCharCode(typeCode)}${String.fromCharCode(data.length)}${data}`;
  
  return result;
}

/**
 * Generate a complete receipt from structured content
 */
export function encodeReceipt(lines: Array<{
  type: 'text' | 'line' | 'qrcode' | 'barcode' | 'space';
  content?: string;
  options?: EncoderOptions;
  size?: number;
  barcodeType?: 'EAN13' | 'CODE128' | 'CODE39';
}>): string {
  let result = ESCPOS.INIT;
  
  for (const line of lines) {
    switch (line.type) {
      case 'text':
        result += encodeText(line.content || '', line.options);
        break;
      case 'line':
        result += encodeLine(line.content || '-');
        break;
      case 'qrcode':
        result += ESCPOS.ALIGN_CENTER;
        result += encodeQrCode(line.content || '', line.size || 4);
        result += LF;
        break;
      case 'barcode':
        result += ESCPOS.ALIGN_CENTER;
        result += encodeBarcode(line.content || '', line.barcodeType || 'CODE128');
        result += LF;
        break;
      case 'space':
        result += ESCPOS.FEED_LINES(line.size || 1);
        break;
    }
  }
  
  return result;
}

/**
 * Generate cut command
 */
export function encodeCut(feedLines: number = 3): string {
  return ESCPOS.FEED_LINES(feedLines) + ESCPOS.CUT_PARTIAL;
}

/**
 * Generate cash drawer kick command
 */
export function encodeOpenDrawer(): string {
  return ESCPOS.DRAWER_KICK_2;
}
